import React, { useEffect, useState, useMemo } from 'react';
import api from './api';
import Spinner from './Spinner';
import HamburgerMenu from './HamburgerMenu';
import CustomAlert from './CustomAlert';
import { Link } from 'react-router-dom';
import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    return {
        page: { backgroundColor: isDark ? '#121212' : '#f4f7f6', minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '100%', margin: '0 auto', fontFamily: 'sans-serif' },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
        title: { fontSize: '24px', margin: 0, color: isDark ? '#e0e0e0' : '#333' },
        card: {
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            border: `1px solid ${isDark ? '#444' : '#ddd'}`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
        },
        tabs: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
        tab: (active) => ({
            padding: '10px 16px',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: active ? '#007bff' : isDark ? '#333' : '#e9ecef',
            color: active ? '#fff' : isDark ? '#e0e0e0' : '#333',
        }),
        label: { fontWeight: 'bold', marginRight: '8px', color: isDark ? '#e0e0e0' : '#333' },
        select: { padding: '8px', marginRight: '12px', borderRadius: '4px', maxWidth: '220px' },
        input: { padding: '8px', marginRight: '12px', borderRadius: '4px' },
        table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
        th: { textAlign: 'left', borderBottom: `2px solid ${isDark ? '#555' : '#ccc'}`, padding: '8px' },
        td: { borderBottom: `1px solid ${isDark ? '#444' : '#eee'}`, padding: '8px', verticalAlign: 'top' },
        chip: {
            display: 'inline-block',
            background: isDark ? '#333' : '#eef',
            borderRadius: '12px',
            padding: '2px 8px',
            margin: '2px',
            fontSize: '11px',
        },
        errorRow: { backgroundColor: isDark ? '#3d2020' : '#ffe6e6' },
        warnRow: { backgroundColor: isDark ? '#3d3520' : '#fff8e6' },
        btn: {
            padding: '6px 10px',
            backgroundColor: '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
        },
        link: { color: '#007bff', textDecoration: 'none' },
        ganttWrap: {
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: '484px',
            border: `1px solid ${isDark ? '#444' : '#ddd'}`,
            borderRadius: '6px',
        },
        ganttRoot: { position: 'relative', minWidth: '2200px' },
        ganttAxis: {
            position: 'sticky',
            top: 0,
            display: 'flex',
            borderBottom: `1px solid ${isDark ? '#555' : '#ccc'}`,
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            fontSize: '11px',
            color: isDark ? '#bbb' : '#555',
            zIndex: 1,
        },
        ganttRow: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${isDark ? '#333' : '#eee'}`,
            height: '44px',
        },
        ganttRowLabel: {
            width: '110px',
            flex: '0 0 110px',
            padding: '0 8px',
            fontSize: '12px',
            fontWeight: 'bold',
            color: isDark ? '#e0e0e0' : '#333',
            backgroundColor: isDark ? '#1e1e1e' : '#fafafa',
            position: 'sticky',
            left: 0,
            zIndex: 2,
            borderRight: `1px solid ${isDark ? '#444' : '#ddd'}`,
        },
        ganttTrack: { position: 'relative', flex: 1, height: '100%' },
        ganttBarSched: (color, overlap, canceled) => ({
            position: 'absolute',
            top: '4px',
            height: '15px',
            backgroundColor: canceled ? (isDark ? '#555' : '#bdbdbd') : color,
            color: '#fff',
            border: overlap ? '2px solid #ff3b30' : `1px solid ${isDark ? '#000' : '#444'}`,
            borderRadius: '3px',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            padding: '0 4px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            cursor: 'default',
            textDecoration: canceled ? 'line-through' : 'none',
            opacity: canceled ? 0.7 : 0.85,
        }),
        ganttBarTurn: (color, eaten) => ({
            position: 'absolute',
            top: '4px',
            height: '15px',
            backgroundColor: 'transparent',
            backgroundImage: `repeating-linear-gradient(135deg, ${color}33 0 5px, transparent 5px 10px)`,
            border: eaten ? '1.5px dashed #ff3b30' : `1px dashed ${isDark ? '#888' : '#666'}`,
            borderLeft: 'none',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: '3px',
            borderBottomRightRadius: '3px',
            pointerEvents: 'auto',
        }),
        ganttBarActual: (color, overlap) => ({
            position: 'absolute',
            top: '23px',
            height: '15px',
            backgroundColor: color,
            color: '#fff',
            border: overlap ? '2px solid #ff3b30' : `1px solid ${isDark ? '#000' : '#222'}`,
            borderRadius: '3px',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            padding: '0 4px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            cursor: 'default',
            backgroundImage: `repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 4px, transparent 4px 8px)`,
        }),
        ganttGrid: (left, onHour) => ({
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${left}%`,
            width: '1px',
            backgroundColor: onHour ? (isDark ? '#555' : '#bbb') : (isDark ? '#2a2a2a' : '#f0f0f0'),
            pointerEvents: 'none',
        }),
        ganttLegend: { fontSize: '12px', color: isDark ? '#bbb' : '#555', marginTop: '8px', display: 'flex', gap: '14px', flexWrap: 'wrap' },
        gridMetricRow: {
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            backgroundColor: isDark ? '#1e1e1e' : '#f7f9fb',
            border: `1px solid ${isDark ? '#444' : '#ccc'}`,
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '12px',
            flexWrap: 'wrap',
        },
        gridMetric: {
            textAlign: 'center',
            color: isDark ? '#e0e0e0' : '#222',
            minWidth: '110px',
        },
        gridMetricLabel: { fontSize: '12px', color: isDark ? '#aaa' : '#555', textTransform: 'uppercase' },
        gridMetricValue: { fontSize: '22px', fontWeight: 'bold' },
        gridTable: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px',
            tableLayout: 'fixed',
        },
        gridTh: {
            border: `1px solid ${isDark ? '#444' : '#bbb'}`,
            backgroundColor: isDark ? '#2b2b2b' : '#e9eef3',
            color: isDark ? '#eee' : '#222',
            padding: '6px 8px',
            textAlign: 'center',
            fontWeight: 'bold',
        },
        gridTd: {
            border: `1px solid ${isDark ? '#444' : '#bbb'}`,
            padding: '4px 6px',
            verticalAlign: 'top',
            color: isDark ? '#e0e0e0' : '#222',
        },
        gridGoCell: (color, canceled, empty) => ({
            border: `1px solid ${isDark ? '#444' : '#bbb'}`,
            borderLeft: empty ? `1px solid ${isDark ? '#444' : '#bbb'}` : `4px solid ${color}`,
            backgroundColor: empty ? (isDark ? '#1a1a1a' : '#fafafa') : canceled ? (isDark ? '#3d2020' : '#ffe6e6') : `${color}22`,
            padding: '4px 6px',
            verticalAlign: 'top',
            color: isDark ? '#e0e0e0' : '#222',
            fontSize: '11px',
            lineHeight: '1.3',
            textDecoration: canceled ? 'line-through' : 'none',
        }),
        gridStatusDot: (color) => ({
            display: 'inline-block',
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: color,
            border: `1px solid ${isDark ? '#222' : '#666'}`,
        }),
        squadStatsTitle: {
            backgroundColor: isDark ? '#0d2b4a' : '#1f3864',
            color: '#fff',
            fontWeight: 'bold',
            padding: '6px 8px',
            border: `1px solid ${isDark ? '#444' : '#ccc'}`,
            textAlign: 'center',
        },
        squadStatsLabel: {
            backgroundColor: isDark ? '#2b2b2b' : '#fff2cc',
            color: isDark ? '#eee' : '#222',
            padding: '6px 8px',
            border: `1px solid ${isDark ? '#444' : '#bbb'}`,
            fontWeight: 'bold',
        },
        squadStatsCell: {
            padding: '6px 8px',
            border: `1px solid ${isDark ? '#444' : '#bbb'}`,
            textAlign: 'center',
            color: isDark ? '#e0e0e0' : '#222',
        },
        squadStatsTotal: {
            padding: '6px 8px',
            border: `1px solid ${isDark ? '#444' : '#bbb'}`,
            textAlign: 'center',
            color: isDark ? '#fff' : '#222',
            backgroundColor: isDark ? '#1b3a1f' : '#e2efda',
            fontWeight: 'bold',
        },
        gridSchedDot: (color, isLight) => ({
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            backgroundColor: color,
            border: `1px solid ${isDark ? '#111' : '#666'}`,
            color: isLight ? '#222' : '#fff',
            fontWeight: 'bold',
            fontSize: '12px',
        }),
        legendSwatch: (color, border) => ({
            display: 'inline-block',
            width: '14px',
            height: '12px',
            backgroundColor: color,
            border: border || '1px solid #444',
            verticalAlign: 'middle',
            marginRight: '4px',
        }),
    };
};

const SQUADRON_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#0097a7', '#c2185b', '#5d4037', '#455a64'];

function squadronColor(name) {
    if (!name) return SQUADRON_COLORS[0];
    let h = 0;
    for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return SQUADRON_COLORS[h % SQUADRON_COLORS.length];
}

function parseWallClockMs(iso) {
    if (!iso) return NaN;
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (m) {
        return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)).getTime();
    }
    return new Date(iso).getTime();
}

function parseLineGroup(lineNumber) {
    const txt = (lineNumber || '').trim();
    if (!txt) return { group: 'Other', go: '' };
    const parts = txt.split(/\s+/);
    if (parts.length === 1) return { group: parts[0], go: '' };
    const tail = parts[parts.length - 1];
    const goIsNumeric = /^\d+$/.test(tail);
    if (goIsNumeric && parts.length > 1) {
        return { group: parts.slice(0, -1).join(' '), go: tail };
    }
    return { group: txt, go: '' };
}

const TURN_AROUND_MS = 60 * 60 * 1000;

function ScheduleGantt({ rows, theme }) {
    const styles = getStyles(theme);

    const data = useMemo(() => {
        const valid = (rows || []).filter((r) => r.sched_launch && r.sched_land);
        if (!valid.length) return null;

        const groups = {};
        valid.forEach((r) => {
            const { group, go } = parseLineGroup(r.line_number);
            (groups[group] = groups[group] || []).push({ ...r, _group: group, _go: go });
        });

        const allTimes = [];
        valid.forEach((r) => {
            const sLand = parseWallClockMs(r.sched_land);
            allTimes.push(parseWallClockMs(r.sched_launch));
            if (Number.isFinite(sLand)) allTimes.push(sLand + TURN_AROUND_MS);
            if (r.actual_launch) allTimes.push(parseWallClockMs(r.actual_launch));
            if (r.actual_land) allTimes.push(parseWallClockMs(r.actual_land));
        });
        let minT = Math.min(...allTimes);
        let maxT = Math.max(...allTimes);
        const startHour = new Date(minT);
        startHour.setMinutes(0, 0, 0);
        minT = startHour.getTime();
        const endHour = new Date(maxT);
        if (endHour.getMinutes() > 0 || endHour.getSeconds() > 0) {
            endHour.setHours(endHour.getHours() + 1, 0, 0, 0);
        }
        maxT = endHour.getTime();
        const span = Math.max(maxT - minT, 3600000);

        const hourTicks = [];
        for (let t = minT; t <= maxT; t += 900000) hourTicks.push(t);

        const overlapsSched = new Set();
        const overlapsActual = new Set();
        const byBuno = {};
        valid.forEach((r) => {
            if (r.buno_str) (byBuno[r.buno_str] = byBuno[r.buno_str] || []).push(r);
        });
        const intersect = (aS, aE, bS, bE) =>
            Number.isFinite(aS) && Number.isFinite(aE) && Number.isFinite(bS) && Number.isFinite(bE) && aS < bE && bS < aE;
        Object.values(byBuno).forEach((list) => {
            for (let i = 0; i < list.length; i += 1) {
                for (let j = i + 1; j < list.length; j += 1) {
                    const a = list[i];
                    const b = list[j];
                    if (intersect(parseWallClockMs(a.sched_launch), parseWallClockMs(a.sched_land), parseWallClockMs(b.sched_launch), parseWallClockMs(b.sched_land))) {
                        overlapsSched.add(a.id);
                        overlapsSched.add(b.id);
                    }
                    if (a.actual_launch && a.actual_land && b.actual_launch && b.actual_land
                        && intersect(parseWallClockMs(a.actual_launch), parseWallClockMs(a.actual_land), parseWallClockMs(b.actual_launch), parseWallClockMs(b.actual_land))) {
                        overlapsActual.add(a.id);
                        overlapsActual.add(b.id);
                    }
                }
            }
        });

        const sortedGroupKeys = Object.keys(groups).sort();
        sortedGroupKeys.forEach((k) => {
            groups[k].sort((a, b) => parseWallClockMs(a.sched_launch) - parseWallClockMs(b.sched_launch));
        });

        return { groups, sortedGroupKeys, minT, maxT, span, hourTicks, overlapsSched, overlapsActual };
    }, [rows]);

    if (!data) return <p style={{ color: styles.title.color }}>No schedule rows with launch/land times to chart.</p>;

    const { groups, sortedGroupKeys, minT, span, hourTicks, overlapsSched, overlapsActual } = data;

    const fmtTime = (ms) => {
        const d = new Date(ms);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    return (
        <div>
            <div style={styles.ganttWrap}>
                <div style={styles.ganttRoot}>
                    <div style={styles.ganttAxis}>
                        <div style={{ ...styles.ganttRowLabel, height: '24px', fontWeight: 'bold' }}>Line</div>
                        <div style={{ flex: 1, position: 'relative', height: '24px' }}>
                            {hourTicks.map((t, i) => {
                                const left = ((t - minT) / span) * 100;
                                const isFirst = i === 0;
                                const isLast = i === hourTicks.length - 1;
                                const d = new Date(t);
                                const onHour = d.getMinutes() === 0;
                                const label = onHour ? fmtTime(t) : `:${String(d.getMinutes()).padStart(2, '0')}`;
                                return (
                                    <div
                                        key={t}
                                        style={{
                                            position: 'absolute',
                                            left: isLast ? undefined : `${left}%`,
                                            right: isLast ? '0' : undefined,
                                            top: 0,
                                            transform: isFirst || isLast ? 'none' : 'translateX(-50%)',
                                            padding: '4px 2px',
                                            fontSize: onHour ? '11px' : '9px',
                                            fontWeight: onHour ? 'bold' : 'normal',
                                            opacity: onHour ? 1 : 0.65,
                                        }}
                                    >
                                        {label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {sortedGroupKeys.map((key) => (
                        <div key={key} style={styles.ganttRow}>
                            <div style={styles.ganttRowLabel}>{key}</div>
                            <div style={styles.ganttTrack}>
                                {hourTicks.map((t) => {
                                    const left = ((t - minT) / span) * 100;
                                    const onHour = new Date(t).getMinutes() === 0;
                                    return <div key={`g-${key}-${t}`} style={styles.ganttGrid(left, onHour)} />;
                                })}
                                {groups[key].map((r) => {
                                    const sMs = parseWallClockMs(r.sched_launch);
                                    const eMs = parseWallClockMs(r.sched_land);
                                    const left = ((sMs - minT) / span) * 100;
                                    const width = Math.max(((eMs - sMs) / span) * 100, 0.5);
                                    const color = squadronColor(r.squadron);
                                    const ovS = !!r.buno_str && overlapsSched.has(r.id);
                                    const ovA = !!r.buno_str && overlapsActual.has(r.id);
                                    const canceled = !!r.canx_code;
                                    const aSMs = parseWallClockMs(r.actual_launch);
                                    const aEMs = parseWallClockMs(r.actual_land);
                                    const hasActual = Number.isFinite(aSMs) && Number.isFinite(aEMs) && aEMs > aSMs;
                                    const aLeft = hasActual ? ((aSMs - minT) / span) * 100 : 0;
                                    const aWidth = hasActual ? Math.max(((aEMs - aSMs) / span) * 100, 0.5) : 0;
                                    const tooltip = [
                                        `Line ${r.line_number}`,
                                        r.squadron || '',
                                        r.buno_str ? `BUNO ${r.buno_str}` : 'No BUNO',
                                        `Sched: ${fmtTime(sMs)} – ${fmtTime(eMs)} (${r.sched_flight_time ?? '?'}h)`,
                                        hasActual
                                            ? `Actual: ${fmtTime(aSMs)} – ${fmtTime(aEMs)} (${r.actual_flight_time ?? '?'}h)`
                                            : 'Actual: not flown',
                                        r.crew_1 || '',
                                        r.tasking_1 || '',
                                        r.canx_code ? `CANX ${r.canx_code}${r.canx_reason ? ` (${r.canx_reason})` : ''}` : '',
                                        ovS ? 'BUNO overlap on scheduled times' : '',
                                        ovA ? 'BUNO overlap on actual times' : '',
                                    ]
                                        .filter(Boolean)
                                        .join('\n');
                                    const turnStart = eMs;
                                    const turnEnd = eMs + TURN_AROUND_MS;
                                    const tLeft = ((turnStart - minT) / span) * 100;
                                    const tWidth = (TURN_AROUND_MS / span) * 100;
                                    const eatenMs = hasActual && aEMs > eMs ? Math.min(aEMs - eMs, TURN_AROUND_MS) : 0;
                                    const eaten = eatenMs > 0;
                                    const turnTooltip = [
                                        `Turn-around (1h): ${fmtTime(turnStart)} – ${fmtTime(turnEnd)}`,
                                        eaten
                                            ? `Actual landed at ${fmtTime(aEMs)} — overran sched by ${Math.round(eatenMs / 60000)} min`
                                            : '',
                                    ]
                                        .filter(Boolean)
                                        .join('\n');
                                    return (
                                        <React.Fragment key={r.id}>
                                            <div
                                                title={tooltip}
                                                style={{
                                                    ...styles.ganttBarSched(color, ovS, canceled),
                                                    left: `${left}%`,
                                                    width: `${width}%`,
                                                }}
                                            >
                                                {r._go ? `${r._go}` : ''}
                                                {r.buno_str ? ` ${r.buno_str}` : ''}
                                            </div>
                                            {!canceled && (
                                                <div
                                                    title={turnTooltip}
                                                    style={{
                                                        ...styles.ganttBarTurn(color, eaten),
                                                        left: `${tLeft}%`,
                                                        width: `${tWidth}%`,
                                                    }}
                                                />
                                            )}
                                            {hasActual && (
                                                <div
                                                    title={tooltip}
                                                    style={{
                                                        ...styles.ganttBarActual(color, ovA),
                                                        left: `${aLeft}%`,
                                                        width: `${aWidth}%`,
                                                    }}
                                                >
                                                    {r.actual_flight_time != null ? `${r.actual_flight_time}h` : ''}
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div style={styles.ganttLegend}>
                <span><span style={{ ...styles.legendSwatch('#1976d2'), opacity: 0.85 }} /> Top stripe = scheduled (color = squadron)</span>
                <span>
                    <span
                        style={{
                            ...styles.legendSwatch('#1976d2'),
                            backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.35) 0 3px, transparent 3px 6px)',
                        }}
                    />{' '}
                    Bottom stripe = actual flown
                </span>
                <span><span style={styles.legendSwatch('#bdbdbd')} /> Canceled (no actual)</span>
                <span>
                    <span
                        style={{
                            ...styles.legendSwatch('transparent', `1px dashed ${theme === 'dark' ? '#888' : '#666'}`),
                            backgroundImage: 'repeating-linear-gradient(135deg, rgba(25,118,210,0.2) 0 4px, transparent 4px 8px)',
                        }}
                    />{' '}
                    1-hr turn-around buffer
                </span>
                <span><span style={styles.legendSwatch('transparent', '1.5px dashed #ff3b30')} /> Actual ate into turn-around</span>
                <span><span style={styles.legendSwatch('transparent', '2px solid #ff3b30')} /> Same BUNO overlaps another sortie</span>
            </div>
        </div>
    );
}

const GO_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

function SortieGrid({ rows, theme }) {
    const styles = getStyles(theme);

    const data = useMemo(() => {
        const groups = {};
        const groupOrder = [];
        (rows || []).forEach((r) => {
            const { group, go } = parseLineGroup(r.line_number);
            const goNum = parseInt(go, 10) || 1;
            if (!groups[group]) {
                groups[group] = { goes: {}, squadron: r.squadron || '' };
                groupOrder.push(group);
            }
            groups[group].goes[goNum] = r;
            if (!groups[group].squadron && r.squadron) groups[group].squadron = r.squadron;
        });

        let maxGo = 0;
        Object.values(groups).forEach((g) => {
            Object.keys(g.goes).forEach((k) => {
                const n = parseInt(k, 10);
                if (n > maxGo) maxGo = n;
            });
        });
        maxGo = Math.max(maxGo, 5);

        const totalSorties = (rows || []).length;
        const uniqueBunos = new Set();
        (rows || []).forEach((r) => {
            if (r.buno_str) uniqueBunos.add(r.buno_str);
        });
        const lineCount = groupOrder.length;
        const acScheduled = uniqueBunos.size || lineCount;
        const sortPerAc = acScheduled ? totalSorties / acScheduled : 0;

        const lines = groupOrder
            .map((k) => {
                const goNums = Object.keys(groups[k].goes)
                    .map((n) => parseInt(n, 10))
                    .sort((a, b) => a - b);
                const hours = goNums
                    .map((n) => {
                        const r = groups[k].goes[n];
                        if (!r.sched_launch) return '--';
                        const m = String(r.sched_launch).match(/T(\d{2}):(\d{2})/);
                        return m ? `${m[1]}:${m[2]}` : '--';
                    })
                    .join('-');
                const fh = goNums.reduce(
                    (sum, n) => sum + (parseFloat(groups[k].goes[n].sched_flight_time) || 0),
                    0,
                );
                const flown = goNums.filter((n) => !!groups[k].goes[n].actual_flight_time).length;
                const canceled = goNums.filter((n) => !!groups[k].goes[n].canx_code).length;
                const buno = goNums.map((n) => groups[k].goes[n].buno_str).find(Boolean) || '';
                return {
                    key: k,
                    squadron: groups[k].squadron,
                    goes: groups[k].goes,
                    goNums,
                    hours,
                    fh,
                    sched: goNums.length,
                    flown,
                    canceled,
                    buno,
                };
            })
            .sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));

        return { lines, maxGo, totalSorties, acScheduled, lineCount, sortPerAc };
    }, [rows]);

    if (!data.lines.length) {
        return <p style={{ color: styles.title.color }}>No sorties to display.</p>;
    }

    const { lines, maxGo, totalSorties, acScheduled, sortPerAc } = data;

    return (
        <div>
            <div style={styles.gridMetricRow}>
                <div style={styles.gridMetric}>
                    <div style={styles.gridMetricLabel}>A/C Sched</div>
                    <div style={styles.gridMetricValue}>{acScheduled}</div>
                </div>
                <div style={styles.gridMetric}>
                    <div style={styles.gridMetricLabel}>Sorties</div>
                    <div style={styles.gridMetricValue}>{totalSorties}</div>
                </div>
                <div style={styles.gridMetric}>
                    <div style={styles.gridMetricLabel}>Sort/AC</div>
                    <div style={styles.gridMetricValue}>{sortPerAc.toFixed(1)}</div>
                </div>
                <div style={styles.gridMetric}>
                    <div style={styles.gridMetricLabel}>Lines</div>
                    <div style={styles.gridMetricValue}>{data.lineCount}</div>
                </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={styles.gridTable}>
                    <colgroup>
                        <col style={{ width: '70px' }} />
                        <col style={{ width: '170px' }} />
                        <col style={{ width: '70px' }} />
                        {Array.from({ length: maxGo }).map((_, i) => (
                            <col key={`c-${i}`} />
                        ))}
                        <col style={{ width: '60px' }} />
                        <col style={{ width: '70px' }} />
                        <col style={{ width: '70px' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={styles.gridTh}>Line</th>
                            <th style={styles.gridTh}>Hours</th>
                            <th style={styles.gridTh}>BUNO</th>
                            {Array.from({ length: maxGo }).map((_, i) => (
                                <th key={`h-${i}`} style={styles.gridTh}>
                                    {GO_LETTERS[i] || `Go ${i + 1}`}
                                </th>
                            ))}
                            <th style={styles.gridTh}>Sched</th>
                            <th style={styles.gridTh}>Status</th>
                            <th style={styles.gridTh}>Est FH</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line) => {
                            let statusColor = '#28a745';
                            let statusTitle = 'On track';
                            if (line.canceled === line.sched && line.sched > 0) {
                                statusColor = '#dc3545';
                                statusTitle = 'All canceled';
                            } else if (line.canceled > 0) {
                                statusColor = '#ffc107';
                                statusTitle = `${line.canceled} canceled, ${line.flown} flown`;
                            } else if (line.flown === line.sched && line.sched > 0) {
                                statusColor = '#17a2b8';
                                statusTitle = 'All flown';
                            }
                            return (
                                <tr key={line.key}>
                                    <td style={{ ...styles.gridTd, fontWeight: 'bold', backgroundColor: `${squadronColor(line.squadron)}33` }}>
                                        {line.key}
                                    </td>
                                    <td style={{ ...styles.gridTd, fontFamily: 'monospace', fontSize: '11px' }}>{line.hours}</td>
                                    <td style={styles.gridTd}>
                                        {line.buno ? (
                                            <Link style={styles.link} to={`/status-board?buno=${encodeURIComponent(line.buno)}`}>
                                                {line.buno}
                                            </Link>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                    {Array.from({ length: maxGo }).map((_, i) => {
                                        const goNum = i + 1;
                                        const r = line.goes[goNum];
                                        if (!r) {
                                            return <td key={`g-${line.key}-${goNum}`} style={styles.gridGoCell('#888', false, true)}>&nbsp;</td>;
                                        }
                                        const color = squadronColor(r.squadron);
                                        const canceled = !!r.canx_code;
                                        const tasking = r.tasking_1 || r.tasking_2 || '';
                                        const crew = r.crew_1 || r.crew_2 || '';
                                        const launchHr = r.sched_launch ? String(r.sched_launch).slice(11, 16) : '';
                                        const tooltip = [
                                            `Line ${r.line_number}`,
                                            r.squadron,
                                            r.buno_str ? `BUNO ${r.buno_str}` : 'No BUNO',
                                            launchHr ? `Launch ${launchHr}` : '',
                                            r.sched_flight_time ? `Sched ${r.sched_flight_time}h` : '',
                                            tasking,
                                            crew,
                                            r.tasking_2 && r.tasking_2 !== r.tasking_1 ? r.tasking_2 : '',
                                            r.crew_2 && r.crew_2 !== r.crew_1 ? r.crew_2 : '',
                                            r.canx_code ? `CANX ${r.canx_code}${r.canx_reason ? ` (${r.canx_reason})` : ''}` : '',
                                        ]
                                            .filter(Boolean)
                                            .join('\n');
                                        return (
                                            <td
                                                key={`g-${line.key}-${goNum}`}
                                                title={tooltip}
                                                style={styles.gridGoCell(color, canceled, false)}
                                            >
                                                <div style={{ fontWeight: 'bold' }}>
                                                    {goNum} - {tasking || '—'}
                                                </div>
                                                <div>{crew}</div>
                                                {r.canx_code && (
                                                    <div style={{ fontSize: '10px', color: '#dc3545' }}>CANX {r.canx_code}</div>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td style={{ ...styles.gridTd, textAlign: 'center' }}>
                                        {(() => {
                                            const count = line.sched;
                                            let color = '#ffffff';
                                            let isLight = true;
                                            if (count >= 4) {
                                                color = '#2e7d32';
                                                isLight = false;
                                            } else if (count === 3) {
                                                color = '#f9a825';
                                                isLight = false;
                                            } else if (count === 2) {
                                                color = '#d84315';
                                                isLight = false;
                                            }
                                            return (
                                                <span
                                                    title={`${count} scheduled go${count === 1 ? '' : 'es'}`}
                                                    style={styles.gridSchedDot(color, isLight)}
                                                >
                                                    {count}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td style={{ ...styles.gridTd, textAlign: 'center' }} title={statusTitle}>
                                        <span style={styles.gridStatusDot(statusColor)} />
                                    </td>
                                    <td style={{ ...styles.gridTd, textAlign: 'right' }}>
                                        {line.fh ? `${line.fh.toFixed(1)} hrs` : '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SquadronStats({ rows, theme, scheduleDate }) {
    const styles = getStyles(theme);

    const data = useMemo(() => {
        const bySquad = {};
        (rows || []).forEach((r) => {
            const sq = r.squadron || 'Unknown';
            if (!bySquad[sq]) bySquad[sq] = { events: [], bunos: new Set(), fh: 0 };
            bySquad[sq].events.push(r);
            if (r.buno_str) bySquad[sq].bunos.add(r.buno_str);
            bySquad[sq].fh += parseFloat(r.sched_flight_time) || 0;
        });
        const squads = Object.keys(bySquad).sort();

        const stats = {};
        squads.forEach((sq) => {
            const ac = bySquad[sq].bunos.size;
            const sorties = bySquad[sq].events.length;
            stats[sq] = {
                ac,
                sorties,
                avg: ac ? sorties / ac : 0,
                fh: bySquad[sq].fh,
            };
        });

        const totalAc = squads.reduce((s, sq) => s + stats[sq].ac, 0);
        const totalSorties = squads.reduce((s, sq) => s + stats[sq].sorties, 0);
        const totalFh = squads.reduce((s, sq) => s + stats[sq].fh, 0);
        const totalAvg = totalAc ? totalSorties / totalAc : 0;

        const SLOTS_PER_DAY = 48;
        const slotSets = {};
        squads.forEach((sq) => {
            slotSets[sq] = Array.from({ length: SLOTS_PER_DAY }, () => new Set());
        });
        let minSlot = SLOTS_PER_DAY;
        let maxSlot = -1;
        const TURN_AROUND_MIN = 60;
        (rows || []).forEach((r) => {
            if (!r.sched_launch || !r.sched_land) return;
            const sM = String(r.sched_launch).match(/T(\d{2}):(\d{2})/);
            const eM = String(r.sched_land).match(/T(\d{2}):(\d{2})/);
            if (!sM || !eM) return;
            const sMins = parseInt(sM[1], 10) * 60 + parseInt(sM[2], 10);
            const baseEndMins = parseInt(eM[1], 10) * 60 + parseInt(eM[2], 10);
            const eMins = r.canx_code ? baseEndMins : baseEndMins + TURN_AROUND_MIN;
            const sSlot = Math.floor(sMins / 30);
            const eSlot = Math.ceil(eMins / 30);
            const sq = r.squadron || 'Unknown';
            const acId = r.buno_str || parseLineGroup(r.line_number).group;
            if (!acId) return;
            for (let s = sSlot; s < eSlot && s < SLOTS_PER_DAY; s += 1) {
                if (s >= 0) {
                    slotSets[sq][s].add(acId);
                    if (s < minSlot) minSlot = s;
                    if (s > maxSlot) maxSlot = s;
                }
            }
        });
        const bySlot = {};
        squads.forEach((sq) => {
            bySlot[sq] = slotSets[sq].map((set) => set.size);
        });
        if (maxSlot < minSlot) {
            minSlot = 12;
            maxSlot = 44;
        }

        return { squads, stats, totalAc, totalSorties, totalFh, totalAvg, bySlot, minSlot, maxSlot };
    }, [rows]);

    if (!data.squads.length) {
        return <p style={{ color: styles.title.color }}>No schedule data to summarize.</p>;
    }

    const slotLabels = [];
    for (let s = data.minSlot; s <= data.maxSlot; s += 1) {
        const h = Math.floor(s / 2);
        const m = (s % 2) * 30;
        slotLabels.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
    const trim = (arr) => arr.slice(data.minSlot, data.maxSlot + 1);

    const stackedData = {
        labels: slotLabels,
        datasets: data.squads.map((sq) => ({
            label: sq,
            data: trim(data.bySlot[sq]),
            backgroundColor: squadronColor(sq),
            stack: 'allocation',
            borderWidth: 0,
        })),
    };
    const stackedOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', labels: { color: styles.title.color } },
            title: { display: true, text: 'Aircraft in use by 30-min slot — incl. 1h turn-around (stacked by squadron)', color: styles.title.color },
        },
        scales: {
            x: { stacked: true, ticks: { color: styles.title.color, maxRotation: 60, minRotation: 60, autoSkip: true } },
            y: {
                stacked: true,
                beginAtZero: true,
                title: { display: true, text: 'Aircraft in use', color: styles.title.color },
                ticks: { color: styles.title.color, precision: 0 },
            },
        },
    };

    return (
        <div>
            <h3 style={{ marginTop: 0, marginBottom: 8, color: styles.title.color }}>
                Executed Schedule for {scheduleDate || ''}
            </h3>
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                <table style={{ ...styles.gridTable, tableLayout: 'auto' }}>
                    <thead>
                        <tr>
                            <th style={styles.squadStatsTitle} colSpan={data.squads.length + 2}>
                                Squadron Statistics
                            </th>
                        </tr>
                        <tr>
                            <th style={styles.squadStatsLabel}>&nbsp;</th>
                            {data.squads.map((sq) => (
                                <th key={`h-${sq}`} style={{ ...styles.squadStatsCell, fontWeight: 'bold', backgroundColor: `${squadronColor(sq)}33` }}>
                                    {sq}
                                </th>
                            ))}
                            <th style={{ ...styles.squadStatsTotal }}>Total TAR</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={styles.squadStatsLabel}>AC Scheduled</td>
                            {data.squads.map((sq) => (
                                <td key={`ac-${sq}`} style={styles.squadStatsCell}>{data.stats[sq].ac}</td>
                            ))}
                            <td style={styles.squadStatsTotal}>{data.totalAc}</td>
                        </tr>
                        <tr>
                            <td style={styles.squadStatsLabel}>Avg Sorties / AC</td>
                            {data.squads.map((sq) => (
                                <td key={`avg-${sq}`} style={styles.squadStatsCell}>{data.stats[sq].avg.toFixed(1)}</td>
                            ))}
                            <td style={styles.squadStatsTotal}>{data.totalAvg.toFixed(1)}</td>
                        </tr>
                        <tr>
                            <td style={styles.squadStatsLabel}># Sorties</td>
                            {data.squads.map((sq) => (
                                <td key={`s-${sq}`} style={styles.squadStatsCell}>{data.stats[sq].sorties}</td>
                            ))}
                            <td style={styles.squadStatsTotal}>{data.totalSorties}</td>
                        </tr>
                        <tr>
                            <td style={styles.squadStatsLabel}>Scheduled FCT HRS</td>
                            {data.squads.map((sq) => (
                                <td key={`fh-${sq}`} style={styles.squadStatsCell}>{data.stats[sq].fh.toFixed(1)}</td>
                            ))}
                            <td style={styles.squadStatsTotal}>{data.totalFh.toFixed(1)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={{ height: '320px', marginBottom: 24 }}>
                <Bar data={stackedData} options={stackedOptions} />
            </div>

            <h3 style={{ marginTop: 12, marginBottom: 8, color: styles.title.color }}>Per-squadron utilization</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '16px' }}>
                {data.squads.map((sq) => {
                    const single = {
                        labels: slotLabels,
                        datasets: [
                            {
                                label: sq,
                                data: trim(data.bySlot[sq]),
                                backgroundColor: squadronColor(sq),
                                borderWidth: 0,
                            },
                        ],
                    };
                    const opts = {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: false },
                            title: { display: true, text: `${sq} — Aircraft in use by 30-min slot (incl. 1h turn)`, color: styles.title.color },
                        },
                        scales: {
                            x: { ticks: { color: styles.title.color, maxRotation: 60, minRotation: 60, autoSkip: true } },
                            y: { beginAtZero: true, ticks: { color: styles.title.color, precision: 0 } },
                        },
                    };
                    return (
                        <div key={`per-${sq}`} style={{ height: '220px' }}>
                            <Bar data={single} options={opts} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function FlightPlanningPage({ theme }) {
    const styles = getStyles(theme);
    const [sites, setSites] = useState([]);
    const [tmsOptions, setTmsOptions] = useState([]);
    const [siteId, setSiteId] = useState('');
    const [tmsId, setTmsId] = useState('');
    const [scheduleDate, setScheduleDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [utilStart, setUtilStart] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 14);
        return d.toISOString().slice(0, 10);
    });
    const [utilEnd, setUtilEnd] = useState(() => new Date().toISOString().slice(0, 10));
    const [tab, setTab] = useState('schedule');

    const [scheduleRows, setScheduleRows] = useState([]);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [tailData, setTailData] = useState([]);
    const [tailLoading, setTailLoading] = useState(false);
    const [conflicts, setConflicts] = useState([]);
    const [conflictLoading, setConflictLoading] = useState(false);
    const [utilization, setUtilization] = useState(null);
    const [utilLoading, setUtilLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    useEffect(() => {
        api.get('/sites/')
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : res.data.results || [];
                setSites(data);
                if (data.length && !siteId) setSiteId(String(data[0].id));
            })
            .catch(() => setSites([]));
        api.get('/tms/')
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : res.data.results || [];
                setTmsOptions(data);
                const tmsMatch = data.find((t) => t.name);
                if (tmsMatch && !tmsId) setTmsId(String(tmsMatch.id));
            })
            .catch(() => setTmsOptions([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSchedule = () => {
        if (!siteId || !scheduleDate) return;
        setScheduleLoading(true);
        api.get('/flight-schedule-events/by_date/', {
            params: { site: siteId, date: scheduleDate, page_size: 500 },
        })
            .then((res) => {
                const rows = res.data.results || res.data;
                setScheduleRows(Array.isArray(rows) ? rows : []);
            })
            .catch((err) => {
                setAlert({ show: true, message: err.response?.data?.error || 'Failed to load schedule', type: 'error' });
                setScheduleRows([]);
            })
            .finally(() => setScheduleLoading(false));
    };

    const loadTails = () => {
        if (!siteId || !tmsId || !scheduleDate) return;
        setTailLoading(true);
        api.get('/flight-schedule-events/recommend_tails/', {
            params: { site: siteId, tms: tmsId, date: scheduleDate },
        })
            .then((res) => setTailData(res.data.lines || []))
            .catch((err) => {
                setAlert({ show: true, message: err.response?.data?.error || 'Tail helper failed', type: 'error' });
                setTailData([]);
            })
            .finally(() => setTailLoading(false));
    };

    const loadConflicts = () => {
        if (!siteId || !scheduleDate) return;
        setConflictLoading(true);
        api.get('/flight-schedule-events/conflicts/', {
            params: { site: siteId, date: scheduleDate },
        })
            .then((res) => setConflicts(res.data.conflicts || []))
            .catch(() => setConflicts([]))
            .finally(() => setConflictLoading(false));
    };

    const loadUtilization = () => {
        if (!siteId || !utilStart || !utilEnd) return;
        setUtilLoading(true);
        api.get('/flight-schedule-events/utilization/', {
            params: { site: siteId, start: utilStart, end: utilEnd },
        })
            .then((res) => setUtilization(res.data))
            .catch(() => setUtilization(null))
            .finally(() => setUtilLoading(false));
    };

    useEffect(() => {
        if (tab === 'schedule') loadSchedule();
        if (tab === 'tails') loadTails();
        if (tab === 'conflicts') loadConflicts();
        if (tab === 'utilization') loadUtilization();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, siteId, tmsId, scheduleDate, utilStart, utilEnd]);

    const assignTail = (eventId, buno) => {
        api.patch(`/flight-schedule-events/${eventId}/`, { buno })
            .then(() => {
                setAlert({ show: true, message: `Assigned ${buno}`, type: 'success' });
                loadTails();
                loadSchedule();
            })
            .catch((err) =>
                setAlert({
                    show: true,
                    message: err.response?.data?.detail || err.response?.data?.error || 'Assign failed',
                    type: 'error',
                })
            );
    };

    const conflictRows = conflicts.map((c, i) => (
        <tr
            key={i}
            style={
                c.severity === 'error' ? styles.errorRow : styles.warnRow
            }
        >
            <td style={styles.td}>{c.severity}</td>
            <td style={styles.td}>{c.type}</td>
            <td style={styles.td}>
                {c.buno ? (
                    <Link style={styles.link} to={`/status-board?buno=${encodeURIComponent(c.buno)}`}>
                        {c.buno}
                    </Link>
                ) : (
                    '—'
                )}
            </td>
            <td style={styles.td}>{c.message}</td>
            <td style={styles.td}>
                {(c.event_ids || []).map((id) => (
                    <span key={id}>#{id} </span>
                ))}
            </td>
        </tr>
    ));

    const utilCharts = useMemo(() => {
        if (!utilization || !utilization.by_squadron?.length) return null;
        const labels = utilization.by_squadron.map((s) => s.squadron);
        const planned = utilization.by_squadron.map((s) => s.events_scheduled);
        const flown = utilization.by_squadron.map((s) => s.events_flown);
        const canceled = utilization.by_squadron.map((s) => s.events_canceled);
        const barData = {
            labels,
            datasets: [
                { label: 'Scheduled lines', data: planned, backgroundColor: '#17a2b8' },
                { label: 'Flown', data: flown, backgroundColor: '#28a745' },
                { label: 'Canceled', data: canceled, backgroundColor: '#dc3545' },
            ],
        };
        const pieLabels = Object.keys(utilization.cancellation_breakdown || {});
        const pieData = {
            labels: pieLabels,
            datasets: [
                {
                    data: pieLabels.map((k) => utilization.cancellation_breakdown[k]),
                    backgroundColor: ['#dc3545', '#ffc107', '#17a2b8', '#6f42c1', '#fd7e14', '#20c997'],
                },
            ],
        };
        return { barData, pieData };
    }, [utilization]);

    const chartOpts = useMemo(
        () => ({
            responsive: true,
            plugins: {
                legend: { position: 'bottom' },
                title: { display: false },
            },
        }),
        []
    );

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
                <div style={styles.header}>
                    <HamburgerMenu theme={theme} />
                    <h1 style={styles.title}>Flight Planning</h1>
                    <div style={{ width: 48 }} />
                </div>

                <div style={styles.card}>
                    <div style={{ marginBottom: 12 }}>
                        <span style={styles.label}>Site</span>
                        <select
                            style={styles.select}
                            value={siteId}
                            onChange={(e) => setSiteId(e.target.value)}
                        >
                            {sites.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name}
                                </option>
                            ))}
                        </select>
                        <span style={styles.label}>TMS</span>
                        <select style={styles.select} value={tmsId} onChange={(e) => setTmsId(e.target.value)}>
                            {tmsOptions.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}
                                </option>
                            ))}
                        </select>
                        <span style={styles.label}>Schedule date</span>
                        <input
                            type="date"
                            style={styles.input}
                            value={scheduleDate}
                            onChange={(e) => setScheduleDate(e.target.value)}
                        />
                    </div>
                    <div style={styles.tabs}>
                        {['schedule', 'grid', 'squadron', 'tails', 'conflicts', 'utilization'].map((t) => (
                            <button key={t} type="button" style={styles.tab(tab === t)} onClick={() => setTab(t)}>
                                {t === 'tails'
                                    ? 'Tail Helper'
                                    : t === 'utilization'
                                    ? 'Utilization'
                                    : t === 'grid'
                                    ? 'Sortie Grid'
                                    : t === 'squadron'
                                    ? 'Squadron Stats'
                                    : t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {tab === 'schedule' && (
                    <div style={styles.card}>
                        <button type="button" style={{ ...styles.btn, backgroundColor: '#17a2b8' }} onClick={loadSchedule}>
                            Refresh
                        </button>
                        {scheduleLoading ? (
                            <Spinner />
                        ) : (
                            <>
                                <h3 style={{ marginTop: 16, marginBottom: 8, color: styles.title.color }}>Daily timeline</h3>
                                <ScheduleGantt rows={scheduleRows} theme={theme} />
                                <h3 style={{ marginTop: 24, marginBottom: 8, color: styles.title.color }}>All events</h3>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Line</th>
                                            <th style={styles.th}>Squadron</th>
                                            <th style={styles.th}>BUNO</th>
                                            <th style={styles.th}>Launch</th>
                                            <th style={styles.th}>Land</th>
                                            <th style={styles.th}>Sched hrs</th>
                                            <th style={styles.th}>Tasking</th>
                                            <th style={styles.th}>CANX</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {scheduleRows.map((r) => (
                                            <tr key={r.id}>
                                                <td style={styles.td}>{r.line_number}</td>
                                                <td style={styles.td}>{r.squadron}</td>
                                                <td style={styles.td}>
                                                    {r.buno_str ? (
                                                        <Link style={styles.link} to={`/status-board?buno=${encodeURIComponent(r.buno_str)}`}>
                                                            {r.buno_str}
                                                        </Link>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td style={styles.td}>{r.sched_launch ? String(r.sched_launch).slice(0, 16) : ''}</td>
                                                <td style={styles.td}>{r.sched_land ? String(r.sched_land).slice(0, 16) : ''}</td>
                                                <td style={styles.td}>{r.sched_flight_time ?? ''}</td>
                                                <td style={styles.td}>{r.tasking_1 || ''}</td>
                                                <td style={styles.td}>{r.canx_code || ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            </>
                        )}
                    </div>
                )}

                {tab === 'grid' && (
                    <div style={styles.card}>
                        <button type="button" style={{ ...styles.btn, backgroundColor: '#17a2b8' }} onClick={loadSchedule}>
                            Refresh
                        </button>
                        {scheduleLoading ? (
                            <Spinner />
                        ) : (
                            <div style={{ marginTop: 12 }}>
                                <SortieGrid rows={scheduleRows} theme={theme} />
                            </div>
                        )}
                    </div>
                )}

                {tab === 'squadron' && (
                    <div style={styles.card}>
                        <button type="button" style={{ ...styles.btn, backgroundColor: '#17a2b8' }} onClick={loadSchedule}>
                            Refresh
                        </button>
                        {scheduleLoading ? (
                            <Spinner />
                        ) : (
                            <div style={{ marginTop: 12 }}>
                                <SquadronStats rows={scheduleRows} theme={theme} scheduleDate={scheduleDate} />
                            </div>
                        )}
                    </div>
                )}

                {tab === 'tails' && (
                    <div style={styles.card}>
                        <p style={{ color: styles.title.color }}>
                            Unassigned lines only — recommendations use AMPT-style scoring, phase hours, ACI, and load balancing.
                        </p>
                        {tailLoading ? (
                            <Spinner />
                        ) : (
                            tailData.map((line) => (
                                <div key={line.event_id} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #444' }}>
                                    <strong>
                                        {line.line_number}
                                    </strong>{' '}
                                    {line.squadron} · {line.sched_flight_time ?? '?'}h · {line.crew_1 || ''}
                                    <div style={{ marginTop: 8 }}>
                                        {(line.recommendations || []).map((rec) => (
                                            <div key={rec.buno} style={{ marginTop: 8 }}>
                                                <strong>{rec.buno}</strong> score {rec.score}{' '}
                                                <button type="button" style={styles.btn} onClick={() => assignTail(line.event_id, rec.buno)}>
                                                    Assign
                                                </button>
                                                <div>
                                                    {(rec.reasons || []).map((rs, idx) => (
                                                        <span key={idx} style={styles.chip}>
                                                            {rs}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                        {!line.recommendations?.length && <span>No recommendations (all excluded or assigned).</span>}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {tab === 'conflicts' && (
                    <div style={styles.card}>
                        {conflictLoading ? (
                            <Spinner />
                        ) : (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Severity</th>
                                        <th style={styles.th}>Type</th>
                                        <th style={styles.th}>BUNO</th>
                                        <th style={styles.th}>Message</th>
                                        <th style={styles.th}>Events</th>
                                    </tr>
                                </thead>
                                <tbody>{conflictRows}</tbody>
                            </table>
                        )}
                    </div>
                )}

                {tab === 'utilization' && (
                    <div style={styles.card}>
                        <div style={{ marginBottom: 12 }}>
                            <span style={styles.label}>Start</span>
                            <input type="date" style={styles.input} value={utilStart} onChange={(e) => setUtilStart(e.target.value)} />
                            <span style={styles.label}>End</span>
                            <input type="date" style={styles.input} value={utilEnd} onChange={(e) => setUtilEnd(e.target.value)} />
                            <button type="button" style={{ ...styles.btn, backgroundColor: '#17a2b8' }} onClick={loadUtilization}>
                                Refresh
                            </button>
                        </div>
                        {utilLoading ? (
                            <Spinner />
                        ) : utilization ? (
                            <>
                                <p>
                                    Scheduled lines: <strong>{utilization.events_scheduled}</strong> · Flown:{' '}
                                    <strong>{utilization.events_flown}</strong> · Canceled:{' '}
                                    <strong>{utilization.events_canceled}</strong> · Completion:{' '}
                                    <strong>{(utilization.completion_rate * 100).toFixed(1)}%</strong>
                                </p>
                                {utilCharts && (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 16 }}>
                                        <div>
                                            <Bar data={utilCharts.barData} options={chartOpts} />
                                        </div>
                                        <div>
                                            {utilCharts.pieData.labels.length > 0 ? (
                                                <Pie data={utilCharts.pieData} options={chartOpts} />
                                            ) : (
                                                <p>No cancellations in range.</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <h4>By BUNO</h4>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={styles.th}>BUNO</th>
                                                <th style={styles.th}>Sched</th>
                                                <th style={styles.th}>Flown</th>
                                                <th style={styles.th}>Canx</th>
                                                <th style={styles.th}>Hrs sched</th>
                                                <th style={styles.th}>Hrs flown</th>
                                                <th style={styles.th}>Hrs to phase</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(utilization.by_buno || []).map((row) => (
                                                <tr key={row.buno}>
                                                    <td style={styles.td}>{row.buno}</td>
                                                    <td style={styles.td}>{row.events_scheduled}</td>
                                                    <td style={styles.td}>{row.events_flown}</td>
                                                    <td style={styles.td}>{row.events_canceled}</td>
                                                    <td style={styles.td}>{row.hours_scheduled?.toFixed?.(1)}</td>
                                                    <td style={styles.td}>{row.hours_flown?.toFixed?.(1)}</td>
                                                    <td style={styles.td}>{row.hours_to_phase_remaining ?? '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <p>No data.</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default FlightPlanningPage;
